import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, Link } from 'react-router-dom';
import PublicLayout from '../components/public/PublicLayout';
import { useAuth } from '../App';
import { toast } from 'sonner';
import {
  ArrowLeft, ChevronRight, Loader2, FileText, Calendar, User,
  GraduationCap, Coins, Clock, CheckCircle2, Circle, Upload, X as XIcon, Paperclip,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse strings like "September 2026", "Sept 2026", "Sep 26"
const MONTH_FULL = ['january','february','march','april','may','june','july','august','september','october','november','december'];
const MONTH_SHORT = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const parseIntakeToDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim().toLowerCase();
  const m = s.match(/([a-z]+)\s+(\d{2,4})/);
  if (!m) {
    const yearOnly = s.match(/(\d{4})/);
    if (yearOnly) return new Date(parseInt(yearOnly[1], 10), 0, 1);
    return null;
  }
  const monthRaw = m[1];
  let idx = MONTH_FULL.indexOf(monthRaw);
  if (idx < 0) idx = MONTH_SHORT.indexOf(monthRaw.slice(0, 3));
  if (idx < 0) return null;
  let year = parseInt(m[2], 10);
  if (year < 100) year += 2000;
  return new Date(year, idx, 1);
};

const isFutureIntake = (raw) => {
  const d = parseIntakeToDate(raw);
  if (!d) return true; // unknown → keep
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() >= today.getTime() - 31 * 86400000; // accept current month
};

const Step = ({ index, label, status, total = 4 }) => (
  <li className="flex items-start gap-3" data-testid={`step-${label.toLowerCase().replace(/\s+/g, '-')}-${status}`}>
    <div className="flex flex-col items-center">
      {status === 'done' ? (
        <CheckCircle2 className="w-6 h-6 text-emerald-500" />
      ) : status === 'active' ? (
        <span className="w-6 h-6 rounded-full bg-emerald-500 ring-4 ring-emerald-100 inline-block" />
      ) : (
        <Circle className="w-6 h-6 text-gray-300" />
      )}
      {index < total - 1 && <span className={`w-px flex-1 ${status === 'done' || status === 'active' ? 'bg-emerald-500' : 'bg-gray-200'} my-2 min-h-[40px]`} />}
    </div>
    <p className={`text-sm font-semibold pt-0.5 ${status === 'todo' ? 'text-gray-400' : 'text-[#1A2A4F]'}`}>{label}</p>
  </li>
);

const StatTile = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2">
    <div className="w-9 h-9 rounded-full bg-white text-[#1A2A4F] flex items-center justify-center shrink-0 shadow-sm">
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <p className="text-sm font-bold text-[#1A2A4F] leading-tight">{value || '—'}</p>
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
    </div>
  </div>
);

const StartApplicationPage = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [program, setProgram] = useState(null);
  const [allIntakes, setAllIntakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [ack, setAck] = useState(false);
  const [chosenIntake, setChosenIntake] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Documents step
  const [docs, setDocs] = useState([
    { caption: 'Passport / Photo ID', filename: '', content_type: '', size: 0, data: '', uploading: false, uploaded: false },
  ]);

  const ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.zip,.xlsx,.xls,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,application/zip';
  const ALLOWED = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/zip', 'application/x-zip-compressed',
    'image/jpeg', 'image/png', 'image/webp',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
  ];
  const MAX_BYTES = 10 * 1024 * 1024;

  const readFile = (file) =>
    new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

  const onPickFile = async (idx, file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`"${file.name}" is larger than 10 MB`);
      return;
    }
    if (file.type && !ALLOWED.includes(file.type)) {
      toast.error(`Unsupported file type: ${file.type || 'unknown'}`);
      return;
    }
    // 1) Immediately enter "uploading" state so the user sees feedback.
    const start = Date.now();
    setDocs((arr) => arr.map((d, i) => (i === idx
      ? { ...d, filename: file.name, content_type: file.type || 'application/octet-stream',
          size: file.size, data: '', uploading: true, uploaded: false }
      : d)));
    try {
      // 2) Read the file.
      const dataUrl = await readFile(file);
      // 3) Hold the "uploading" state for at least 600 ms so even tiny
      //    files show a deliberate progress animation.
      const elapsed = Date.now() - start;
      const minVisibleMs = 600;
      if (elapsed < minVisibleMs) {
        await new Promise((r) => setTimeout(r, minVisibleMs - elapsed));
      }
      // 4) Flip to "uploaded" — JSX shows the green tick "Attached" chip.
      setDocs((arr) => arr.map((d, i) => (i === idx
        ? { ...d, data: dataUrl, uploading: false, uploaded: true }
        : d)));
    } catch {
      setDocs((arr) => arr.map((d, i) => (i === idx
        ? { ...d, uploading: false, uploaded: false }
        : d)));
      toast.error('Could not read the file');
    }
  };

  const addDocRow = () => setDocs((arr) => [...arr, { caption: '', filename: '', content_type: '', size: 0, data: '', uploading: false, uploaded: false }]);
  const removeDocRow = (idx) => setDocs((arr) => arr.filter((_, i) => i !== idx));

  useEffect(() => {
    if (!user) {
      const qs = new URLSearchParams({ next: `/apply/${programId}` });
      navigate(`/login?${qs.toString()}`, { replace: true });
    }
  }, [user, programId, navigate]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [pr, intakes] = await Promise.all([
          axios.get(`${API}/master-data/${programId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
          }).catch(() => null),
          axios.get(`${API}/public/master-data?type=intake`).catch(() => ({ data: [] })),
        ]);
        if (!pr) {
          // public fallback: try slug? We don't have slug here. Just fail gracefully.
          setProgram(null);
        } else {
          setProgram(pr.data);
        }
        setAllIntakes(Array.isArray(intakes.data) ? intakes.data : []);
      } catch (e) {
        toast.error('Could not load program');
      }
      setLoading(false);
    };
    if (user) load();
  }, [programId, user]);

  // Intake options = program's primary intake + all admin intakes, filtered future
  const intakeOptions = useMemo(() => {
    const opts = new Set();
    const pm = program?.metadata || {};
    if (pm.intake) opts.add(pm.intake);
    if (pm.session_start) opts.add(pm.session_start);
    for (const it of allIntakes || []) {
      if (it?.name) opts.add(it.name);
    }
    return [...opts].filter(isFutureIntake).sort((a, b) => {
      const da = parseIntakeToDate(a)?.getTime() || 0;
      const db = parseIntakeToDate(b)?.getTime() || 0;
      return da - db;
    });
  }, [program, allIntakes]);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Only send docs that have a file AND a caption
      const cleanDocs = docs
        .filter((d) => d.data && (d.caption || '').trim())
        .map((d) => ({
          caption: d.caption.trim(),
          filename: d.filename || 'attachment',
          content_type: d.content_type || 'application/octet-stream',
          size: d.size || 0,
          data: d.data,
        }));
      const r = await api().post('/student/me/apply', {
        program_id: programId,
        intake: chosenIntake,
        ack_eligibility: true,
        documents: cleanDocs,
      });
      if (r.data?.success) {
        const owner = r.data.assigned_to_name ? ` Assigned to ${r.data.assigned_to_name}.` : '';
        toast.success(`Application submitted.${owner}`);
        navigate('/my-account/applications');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit application');
    }
    setSubmitting(false);
  };

  if (loading || !user) {
    return (
      <PublicLayout>
        <div className="max-w-5xl mx-auto px-4 py-20 text-center text-gray-500" data-testid="apply-loading">
          Loading application…
        </div>
      </PublicLayout>
    );
  }
  if (!program) {
    return (
      <PublicLayout>
        <div className="max-w-5xl mx-auto px-4 py-20 text-center" data-testid="apply-program-missing">
          <h1 className="text-2xl font-bold text-[#1A2A4F]">Program not found</h1>
          <Link to="/courses" className="mt-6 inline-flex items-center gap-1 text-[#F5A623] font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to all courses
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const m = program.metadata || {};
  const tuition = (m.tuition_breakdown && m.tuition_breakdown[0]?.amount)
    ? `${m.currency || ''} ${Number(m.tuition_breakdown[0].amount).toLocaleString()}`
    : (m.annual_tuition_fees_raw || '—');

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8" data-testid="start-application-page">
        {/* Back row */}
        <button onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A2A4F] hover:underline"
          data-testid="apply-back">
          <ArrowLeft className="w-4 h-4" /> Application
        </button>

        {/* Program card */}
        <div className="mt-3 bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="apply-program-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extrabold text-[#1A2A4F]" data-testid="apply-program-title">
                {m.course_title || program.name}
              </h1>
              <p className="mt-1 text-sm text-gray-700">
                At <span className="font-bold text-[#1A2A4F]">{m.university_name || '—'}</span>
              </p>
              {m.intake && (
                <p className="mt-1 text-xs text-gray-500">
                  <span className="font-semibold text-emerald-600">Next intake:</span> {m.intake}
                </p>
              )}
            </div>
            {m.slug && (
              <Link to={`/courses/${m.slug}`}
                className="inline-flex items-center gap-1 text-sm font-semibold text-rose-600 hover:underline"
                data-testid="apply-view-program-link">
                View program details <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>

          {/* Stat strip */}
          <div className="mt-4 bg-gray-50 rounded-md p-3 sm:p-4 flex flex-wrap gap-x-6 gap-y-3">
            <StatTile icon={Coins} label={`Tuition Fee (${m.currency || ''})`} value={tuition} />
            <StatTile icon={Coins} label="Application Fee" value={m.application_fee || '—'} />
            <StatTile icon={Clock} label="Duration" value={m.duration} />
            <StatTile icon={FileText} label="IELTS" value={m.ielts || m.IELTS || '—'} />
            <StatTile icon={FileText} label="TOEFL" value={m.toefl || m.TOEFL || '—'} />
            <StatTile icon={GraduationCap} label="Min GPA" value={m.min_gpa || m.gpa || '—'} />
          </div>
        </div>

        {/* Body: steps + form */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-6">
          <section className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 min-h-[400px]">
            <h2 className="text-xl font-bold text-[#1A2A4F]">
              Great — we&apos;ll help you apply for your chosen program
            </h2>

            {step === 1 && (
              <div className="mt-5 space-y-4" data-testid="apply-step-eligibility">
                <p className="text-sm text-gray-700">
                  Make sure you&apos;re eligible for this program before you start your application.
                </p>
                <div className="space-y-3">
                  <h3 className="font-bold text-[#1A2A4F]">Admission Requirements</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {m.admission_requirements
                      || m.eligibility
                      || 'No specific admission requirements listed for this program. Please contact the university to confirm.'}
                  </p>

                  <h3 className="font-bold text-[#1A2A4F] pt-2">
                    You have access to the supporting documents you&apos;ll need to upload as part of your application:
                  </h3>
                  <ul className="list-disc ml-5 text-sm text-gray-700 space-y-1">
                    <li>A valid passport or other requested ID</li>
                    <li>Any academic certificates / diplomas</li>
                    <li>English proficiency test results (IELTS / TOEFL), if applicable</li>
                  </ul>
                  <p className="text-sm text-gray-700">
                    You don&apos;t need to have these ready to start your application, but you&apos;ll need to upload them before it&apos;s ready to submit.
                  </p>
                </div>
                <label className="flex items-start gap-3 mt-4 p-3 rounded-md bg-emerald-50 border border-emerald-200 cursor-pointer">
                  <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1 accent-emerald-500" data-testid="apply-ack-eligibility" />
                  <span className="text-sm text-emerald-900">
                    I confirm I have read the admission requirements and meet them for this program.
                  </span>
                </label>
                <div className="flex items-center justify-end pt-3">
                  <button onClick={() => setStep(2)} disabled={!ack}
                    className="bg-[#F5A623] text-white font-bold px-5 py-2.5 rounded-md hover:brightness-95 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="apply-eligibility-next">
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="mt-5 space-y-4" data-testid="apply-step-start-date">
                <h3 className="font-bold text-[#1A2A4F]">Pick your start date (intake)</h3>
                <p className="text-sm text-gray-700">
                  Past intakes are filtered out automatically. Select the session that works best for you.
                </p>
                {intakeOptions.length === 0 ? (
                  <p className="text-sm text-rose-600" data-testid="apply-no-future-intakes">
                    No upcoming intakes have been added yet for this program. Please contact a counsellor.
                  </p>
                ) : (
                  <ul className="space-y-2" data-testid="apply-intake-list">
                    {intakeOptions.map((opt) => (
                      <li key={opt}>
                        <label className={`flex items-center gap-3 border rounded-md px-4 py-3 cursor-pointer transition ${
                          chosenIntake === opt ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-gray-200 hover:bg-gray-50'
                        }`} data-testid={`apply-intake-${opt.replace(/\s+/g, '-')}`}>
                          <input type="radio" name="intake" value={opt} checked={chosenIntake === opt}
                            onChange={() => setChosenIntake(opt)} className="accent-[#F5A623]" />
                          <Calendar className="w-4 h-4 text-[#F5A623]" />
                          <span className="font-semibold text-[#1A2A4F]">{opt}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center justify-between pt-3">
                  <button onClick={() => setStep(1)} className="text-sm font-semibold text-gray-500 hover:underline" data-testid="apply-back-to-elig">
                    ← Back
                  </button>
                  <button onClick={() => setStep(3)} disabled={!chosenIntake}
                    className="bg-[#F5A623] text-white font-bold px-5 py-2.5 rounded-md hover:brightness-95 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="apply-start-date-next">
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="mt-5 space-y-4" data-testid="apply-step-documents">
                <h3 className="font-bold text-[#1A2A4F]">Upload supporting documents</h3>
                <p className="text-sm text-gray-700">
                  Add a short caption for each file (e.g. &quot;Passport copy&quot;, &quot;IELTS score&quot;). You can add more later from My Account.
                </p>

                <ul className="space-y-3" data-testid="apply-docs-list">
                  {docs.map((d, i) => (
                    <li key={i} className="border border-gray-200 rounded-lg p-3 sm:p-4 bg-gray-50/40" data-testid={`apply-doc-row-${i}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                        <input
                          type="text" value={d.caption}
                          onChange={(e) => setDocs((arr) => arr.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))}
                          placeholder="Document caption (e.g. Passport, Transcripts, IELTS)"
                          className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#F5A623]/30 focus:border-[#F5A623] outline-none bg-white"
                          data-testid={`apply-doc-caption-${i}`}
                        />
                        {docs.length > 1 && (
                          <button type="button" onClick={() => removeDocRow(i)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-gray-500 hover:bg-rose-50 hover:text-rose-600"
                            data-testid={`apply-doc-remove-${i}`}
                            aria-label="Remove">
                            <XIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          id={`apply-doc-file-${i}`}
                          type="file" accept={ACCEPT}
                          className="hidden"
                          onChange={(e) => onPickFile(i, e.target.files?.[0])}
                          data-testid={`apply-doc-file-${i}`}
                        />
                        <button type="button"
                          onClick={() => document.getElementById(`apply-doc-file-${i}`)?.click()}
                          disabled={d.uploading}
                          className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-md transition ${
                            d.uploaded
                              ? 'border border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                              : 'border border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10'
                          } disabled:opacity-60`}
                          data-testid={`apply-doc-pick-${i}`}>
                          {d.uploading ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                          ) : d.uploaded ? (
                            <><CheckCircle2 className="w-3.5 h-3.5" /> Replace file</>
                          ) : (
                            <><Upload className="w-3.5 h-3.5" /> Choose file</>
                          )}
                        </button>

                        {d.uploading && (
                          <span className="inline-flex items-center gap-1 text-xs text-[#F5A623]" data-testid={`apply-doc-uploading-${i}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-[#F5A623] animate-pulse" />
                            <span className="truncate max-w-[200px]">{d.filename}</span>
                            <span className="text-gray-400">({Math.ceil((d.size || 0) / 1024)} KB)</span>
                          </span>
                        )}

                        {!d.uploading && d.uploaded && (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 animate-in fade-in slide-in-from-left-2" data-testid={`apply-doc-success-${i}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            <span data-testid={`apply-doc-filename-${i}`} className="truncate max-w-[200px] font-semibold">{d.filename}</span>
                            <span className="text-emerald-500/70">({Math.ceil((d.size || 0) / 1024)} KB)</span>
                            <span className="ml-1 text-[10px] uppercase tracking-wider font-bold">Attached</span>
                          </span>
                        )}
                      </div>
                      {d.uploading && (
                        <div className="mt-2 h-1 bg-[#F5A623]/10 rounded overflow-hidden" data-testid={`apply-doc-progress-${i}`}>
                          <div className="h-full bg-gradient-to-r from-[#F5A623]/60 via-[#F5A623] to-[#F5A623]/60 animate-pulse" />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>

                <div className="flex items-center justify-between">
                  <button type="button" onClick={addDocRow}
                    className="text-xs font-bold text-[#F5A623] hover:underline inline-flex items-center gap-1"
                    data-testid="apply-doc-add-btn">
                    + Add another document
                  </button>
                  <p className="text-[11px] text-gray-500">
                    Supported: PDF · DOC/DOCX · JPG · PNG · ZIP · XLSX — max 10 MB each
                  </p>
                </div>

                <div className="flex items-center justify-between pt-4">
                  <button onClick={() => setStep(2)} className="text-sm font-semibold text-gray-500 hover:underline" data-testid="apply-back-to-intake">
                    ← Back
                  </button>
                  <button onClick={() => setStep(4)}
                    className="bg-[#F5A623] text-white font-bold px-5 py-2.5 rounded-md hover:brightness-95 inline-flex items-center gap-2"
                    data-testid="apply-documents-next">
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="mt-5 space-y-4" data-testid="apply-step-account">
                <h3 className="font-bold text-[#1A2A4F]">Confirm your details</h3>
                <p className="text-sm text-gray-700">
                  We&apos;ll use the account details below to start your application. You can update these later from My Profile.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <Detail label="Name" value={user.name} />
                  <Detail label="Email" value={user.email} />
                  <Detail label="Program" value={m.course_title || program.name} />
                  <Detail label="University" value={m.university_name} />
                  <Detail label="Intake" value={chosenIntake} />
                  <Detail label="Documents" value={`${docs.filter((d) => d.data).length} file(s) attached`} />
                </div>
                <div className="flex items-center justify-between pt-4">
                  <button onClick={() => setStep(3)} className="text-sm font-semibold text-gray-500 hover:underline" data-testid="apply-back-to-docs">
                    ← Back
                  </button>
                  <button onClick={submit} disabled={submitting}
                    className="bg-emerald-500 text-white font-bold px-6 py-2.5 rounded-md hover:brightness-95 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="apply-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Submit Application
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Stepper */}
          <aside className="lg:sticky lg:top-32 h-fit" data-testid="apply-stepper">
            <ul className="bg-white border border-gray-100 rounded-2xl p-5 space-y-2">
              <Step index={0} label="Eligibility" status={step > 1 ? 'done' : 'active'} total={4} />
              <Step index={1} label="Start date" status={step > 2 ? 'done' : step === 2 ? 'active' : 'todo'} total={4} />
              <Step index={2} label="Documents" status={step > 3 ? 'done' : step === 3 ? 'active' : 'todo'} total={4} />
              <Step index={3} label="Account" status={step === 4 ? 'active' : step > 4 ? 'done' : 'todo'} total={4} />
            </ul>
          </aside>
        </div>
      </div>
    </PublicLayout>
  );
};

const Detail = ({ label, value }) => (
  <div className="bg-gray-50 rounded-md px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="font-semibold text-[#1A2A4F]">{value || '—'}</p>
  </div>
);

export default StartApplicationPage;

